import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeToMailingList, fetchCategories } from '../../api';
import Logo from './Logo';
import NewsletterAlert from './NewsletterAlert';
import './footer.css';

// Literal tagline text from the confirmed reference
// (.agent/templates/sonic-motion-reference.html) — no public /settings
// endpoint exists yet to fetch site_description dynamically, so this is the
// reference's own copy rather than an invented placeholder.
const TAGLINE =
  'Yardie a farin, bare vibez. Jamaican conversations, culture, technology, and stories from home and abroad.';
const FOOTER_LOCATION_LINE = 'Kingston, Jamaica · To The Flippin World';

// No site-level social URLs exist in the schema (only per-advertiser and
// per-subscriber socials) — these are placeholders until real handles are
// decided. Swap for real URLs (or wire to new `settings` rows) when ready.
const SOCIAL_LINKS = [
  { name: 'YouTube', href: 'https://youtube.com/@guhso' },
  { name: 'X', href: 'https://x.com/guhsolive?s=11' },
  { name: 'Instagram', href: 'https://www.instagram.com/guhso?igsh=azFmNWQ5aDVnZjlm&igsi=azFmNWQ5aDVnZjlm&utm_source=qr' },
  { name: 'Spotify', href: 'https://open.spotify.com/show/6OB8f8kK4MBfqeHXp1jqZb?si=354b558dcafa45c4' },
  { name: 'TikTok', href: 'https://tiktok.com/@guhsolive' },
];

const SocialIcon = ({ name }) => {
  switch (name) {
    case 'YouTube':
      return (
        <svg viewBox="0 0 24 24" width="17" height="17">
          <rect x="2" y="5" width="20" height="14" rx="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <polygon points="10,9 16,12 10,15" fill="currentColor" />
        </svg>
      );
    case 'X':
      return (
        <svg viewBox="0 0 24 24" width="15" height="15">
          <path d="M3 3l18 18M21 3L3 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case 'Instagram':
      return (
        <svg viewBox="0 0 24 24" width="17" height="17">
          <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" />
        </svg>
      );
    case 'Spotify':
      return (
        <svg viewBox="0 0 24 24" width="17" height="17">
          <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M6.5 10c4-1 8-.6 11 1M6.8 13c3.2-.8 6.5-.5 9 .9M7.2 16c2.6-.6 5-.4 7 .7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      );
    case 'TikTok':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path
            d="M13 3v11.5a3.5 3.5 0 1 1-3-3.46"
            stroke="currentColor"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M13 3c.4 2.4 2.1 4 4.5 4.3" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
};

// Literal markup match of the reference's .a-footer-newsletter form
// (.a-footer-kicker/h4/p/.a-footer-input-row) — the reference's own markup
// has no <label>, just an aria-label on the input, so that's kept as-is
// rather than reintroducing a visible label element.
const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      const result = await subscribeToMailingList('', email);
      setStatus('success');
      setMessage(/already confirmed/i.test(result?.message || '')
        ? 'This email is already subscribed to Guhso updates.'
        : 'Newsletter signup confirmed. Watch your inbox for Guhso updates.');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Try again.');
    }
  };

  return (
    <>
      <form className="a-footer-newsletter" onSubmit={handleSubmit}>
      <span className="a-footer-kicker">Free email newsletter</span>
      <h4>Get new episodes and Guhso updates.</h4>
      <p>No noise. Just the newest conversations, videos, and community stories.</p>
      <div className="a-footer-input-row">
        <input
          aria-label="Email address"
          type="email"
          required
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'loading'}
        />
        <button type="submit" disabled={status === 'loading'}>
          {status === 'loading' ? 'Joining…' : 'Join newsletter'}
        </button>
      </div>
      <p className="a-footer-membership-link">
        Looking for paid subscriber access? <Link to="/register">Become a Guhso member</Link>.
      </p>
      </form>
      <NewsletterAlert
        open={Boolean(message)}
        type={status === 'error' ? 'error' : 'success'}
        message={message}
        onClose={() => setMessage('')}
      />
    </>
  );
};

const CategoryChips = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let isMounted = true;
    fetchCategories()
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setCategories(list);
      })
      .catch((err) => console.error('Failed to load categories:', err));
    return () => {
      isMounted = false;
    };
  }, []);

  if (categories.length === 0) return null;

  return (
    <div className="a-cat-chips">
      {categories.map((cat) => (
        <span key={cat.id} className="a-cat-chip" style={{ '--chip': cat.color || 'var(--color-primary)' }}>
          <span className="sw" />
          {cat.name}
        </span>
      ))}
    </div>
  );
};

// Literal markup match of the reference's .a-footer/.a-footer-inner/
// .a-footer-lead (brand+social | newsletter)/.a-footer-directory (4-col:
// Listen & Watch / Explore / About / Browse by Category). Real
// NewsletterForm (subscribeToMailingList) and CategoryChips
// (fetchCategories) logic preserved — only markup/classes/copy changed to
// match the reference. "Returns/Privacy/Terms" legal links aren't part of
// the reference's footer-bottom (it just has a copyright line + a location
// line) — kept as a small real-functionality addition rather than dropped,
// since those are real routes, not decorative.
const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="a-footer">
      <div className="a-footer-inner">
        <div className="a-footer-lead">
          <div className="a-footer-brand">
            <Logo />
            <p>{TAGLINE}</p>
            <div className="a-footer-social" aria-label="Guhso social links">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  className="a-social-btn"
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  title={social.name}
                  aria-label={social.name}
                >
                  <SocialIcon name={social.name} />
                </a>
              ))}
            </div>
          </div>
          <NewsletterForm />
        </div>

        <div className="a-footer-directory">
          <div className="a-footer-col">
            <h5>Listen &amp; Watch</h5>
            <div className="links">
              <Link to="/episodes">Episodes</Link>
              <a href="#shorts">YouTube Shorts</a>
              {/* No live-radio stream page exists yet. Rendered as inert text
                  rather than href="#", which jumps to top and announces itself
                  as a link. Swap in <Link to="/radio"> when the route lands. */}
              <span className="link-pending" title="Coming soon">Live Radio</span>
              <a href="https://open.spotify.com" target="_blank" rel="noreferrer">Spotify</a>
            </div>
          </div>
          <div className="a-footer-col">
            <h5>Explore</h5>
            <div className="links">
              <Link to="/blog">It Guhso</Link>
              <Link to="/tech">Tech</Link>
              <Link to="/merch">Merch</Link>
              <Link to="/directory">Directory</Link>
            </div>
          </div>
          <div className="a-footer-col">
            <h5>About</h5>
            <div className="links">
              <Link to="/about">What Is Guhso</Link>
              <Link to="/advertise">Advertise With Us</Link>
              {/* No /contact route exists yet — same reasoning as Live
                  Radio above. */}
              <span className="link-pending" title="Coming soon">Contact</span>
              <Link to="/donation">Donate</Link>
            </div>
          </div>
          <div className="a-footer-col a-footer-col--categories">
            <h5>Browse by Category</h5>
            <CategoryChips />
          </div>
        </div>

        <div className="a-footer-bottom">
          <span>© {year} Guhso. All rights reserved.</span>
          <span>{FOOTER_LOCATION_LINE}</span>
          <div className="a-footer-legal">
            <Link to="/returns">Returns</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
